import { useFormContext } from 'react-hook-form'

import { Box, Button, Table, Text } from '@factorialco/deprecated-design-system'

import NumberField from 'modules/core/components/NumberField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { PerDiemRate, usePerDiemForm } from 'modules/expenses/hooks/usePerDiemForm'

import { CustomizationProps } from '../../withCustomizationFields'
import { WithFieldPermissionsProps } from '../../withFieldPermissions'
import PerDiemRateFieldComponent from './PerDiemRateField'

interface Props extends WithFieldPermissionsProps, CustomizationProps {
  loading: boolean
}

const PerDiemRates = (props: Props) => {
  const { currentEmployee, expensable } = props

  const { watch, setValue } = useFormContext()
  const { company, access } = useCurrentSession()
  const perDiemRates = watch('perDiemRates')
  const currency =
    watch('currency') || expensable?.currency || currentEmployee?.legalEntity?.currency
  const deletedRates = watch('deleted_rates')

  const { rateConfigurations, loading: loadingPerDiemForm } = usePerDiemForm(company.id, access.id)

  const handleSelectRate = (rate: PerDiemRate, rateConfigurationId: number | null): PerDiemRate => {
    const rateConfiguration = rateConfigurations?.find((r) => r.id === rateConfigurationId)
    if (!rateConfiguration) return rate

    setValue('currency', rateConfiguration.currency)
    return {
      ...rate,
      rateConfigurationId: rateConfiguration.id,
      totalAmount: rate.units * (rateConfiguration.amountCents || 0),
      rateAmount: rateConfiguration.amountCents || 0,
      label: rateConfiguration.name,
      currency: rateConfiguration.currency,
    }
  }

  const calculateTotalAmount = (rates?: PerDiemRate[]) => {
    const totalAmount = rates?.reduce((acc: number, rate: PerDiemRate) => acc + rate.totalAmount, 0)
    return totalAmount
  }

  const handleAddRate = () => {
    const newRate: PerDiemRate = {
      rateConfigurationId: null,
      units: 1,
      rateAmount: 0,
      totalAmount: 0,
      label: '',
      currency: '',
    }
    setValue('perDiemRates', [...perDiemRates, newRate], {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const handleUpdateRate = (rate: PerDiemRate, units: number | null): PerDiemRate => {
    return {
      ...rate,
      units: units || 0,
      totalAmount: units ? units * rate.rateAmount : 0,
    }
  }

  const formatTotalAmount = () => {
    return formattedAmount({
      amountCents: calculateTotalAmount(perDiemRates),
      currency,
      locale: access.locale,
    })
  }

  const rateIds = perDiemRates
    .map((r: PerDiemRate) => r.rateConfigurationId)
    .filter((item: PerDiemRate) => item)

  const nonSelectedRates = rateConfigurations.filter((r) => !rateIds.includes(r.id))

  const canAddRates = nonSelectedRates.length > 0 && perDiemRates.length < rateConfigurations.length

  return (
    <Table background="grey200">
      <Table.Row>
        <Table.HeadCell colSpan={3} paddingX="s8" paddingY="s8">
          {i18n.t('expenses_v2.per_diem.per_diem_rates.table.rate')}
        </Table.HeadCell>
        <Table.HeadCell colSpan={1} paddingX="s0" paddingY="s8">
          {i18n.t('expenses_v2.per_diem.per_diem_rates.table.units')}
        </Table.HeadCell>
        <Table.HeadCell colSpan={2} paddingX="s8" paddingY="s8">
          {i18n.t('expenses_v2.per_diem.per_diem_rates.table.amount')}
        </Table.HeadCell>
      </Table.Row>

      {perDiemRates.map((rate: PerDiemRate, index: number) => (
        <Table.Row key={index}>
          <Table.Cell colSpan={3} paddingX="s8" paddingY="s8">
            <PerDiemRateFieldComponent
              {...props}
              fieldIdentifier="rates"
              selectedRate={rate}
              isFieldReadOnly={nonSelectedRates.length === 0}
              loading={loadingPerDiemForm}
              onSelect={(rateConfigurationId) => {
                const updatedRates = perDiemRates.map((r: PerDiemRate, i: number): PerDiemRate => {
                  if (i === index) return handleSelectRate(r, rateConfigurationId)
                  else return r
                })
                setValue('perDiemRates', updatedRates, {
                  shouldDirty: true,
                  shouldValidate: true,
                })

                const totalAmount = calculateTotalAmount(updatedRates)
                setValue('amount', totalAmount, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }}
            />
          </Table.Cell>
          <Table.Cell colSpan={1} paddingX="s0" paddingY="s8">
            <NumberField
              hideLabel
              autofocus={false}
              value={rate.units}
              placeholder="0"
              onChange={(units: number | null) => {
                const updatedRates = perDiemRates.map((r: PerDiemRate, i: number): PerDiemRate => {
                  if (i === index) {
                    return handleUpdateRate(r, units)
                  }
                  return r
                })
                setValue('perDiemRates', updatedRates, {
                  shouldDirty: true,
                  shouldValidate: true,
                })

                const totalAmount = calculateTotalAmount(updatedRates)
                setValue('amount', totalAmount, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }}
            />
          </Table.Cell>
          <Table.Cell colSpan={2} paddingX="s8" paddingY="s8">
            <Box flexDirection="row" alignItems="center" gap="s12" justifyContent="flexEnd">
              <NumberField
                hideLabel
                readonly
                autofocus={false}
                value={rate.totalAmount / 100}
                indicator={currency}
                placeholder="0"
              />
              <Box flexDirection="row" justifyContent="flexEnd">
                <Button
                  type="secondary"
                  iconName="delete"
                  disabled={perDiemRates.length === 1}
                  onClick={() => {
                    if (rate.id) {
                      setValue('deleted_rates', [...deletedRates, rate.id], {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }

                    const updatedRates = perDiemRates.filter((_: number, i: number) => i !== index)
                    setValue('perDiemRates', updatedRates, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }}
                />
              </Box>
            </Box>
          </Table.Cell>
        </Table.Row>
      ))}

      {canAddRates && (
        <Table.Row>
          <Table.Cell paddingX="s8" paddingY="s8" colSpan={6}>
            <Button type="secondary" iconName="add" onClick={handleAddRate}>
              {i18n.t('expenses_v2.per_diem.per_diem_rates.table.add_rate')}
            </Button>
          </Table.Cell>
        </Table.Row>
      )}

      <Table.Row>
        <Table.HeadCell colSpan={4} paddingX="s8" paddingY="s8">
          <Text.Normal size="200" className="my-2" weight="semibold">
            {i18n.t('expenses_v2.per_diem.per_diem_rates.table.total')}
          </Text.Normal>
        </Table.HeadCell>

        <Table.HeadCell colSpan={2} paddingX="s8" paddingY="s8" justifyContent="flexEnd">
          <Box className="px-3 py-1 rounded-full" background={'grey400'}>
            <Text.Normal size="200" weight="semibold" color="grey1000">
              {formatTotalAmount()}
            </Text.Normal>
          </Box>
        </Table.HeadCell>
      </Table.Row>
    </Table>
  )
}

export default PerDiemRates
